import React from 'react';
import cx from 'classnames';

import PageLayout from '@magoosh/layouts/page';
import { PageProps } from '@magoosh/layouts/types';

import FAQSection from '@marketing/sections/faq';
import { OfferProps } from '@marketing/types';

import ApplicationSupportSection from './sections/application_support';
import CompetitorComparisonSection from './sections/competitor_comparison';
import HeroSection from './sections/hero';
import OffersSection from './sections/offers';
import SocialProofSection from './sections/social_proof';
import StartYourPrepSection from './sections/start_your_prep';
import TestimonialsSection from './sections/testimonials';

import styles from './style.module.scss';
import marketingStyles from '@marketing/style.module.scss';

interface Props extends PageProps {
  hideSignUp: boolean;
  isLoggedIn: boolean;
  offers: OfferProps[];
}

export default (props: Props) => {
  const admissionsBundleOffer = props.offers.find((offer) => (
    offer.slug === 'premium-admissions-bundle' || offer.slug === 'premium-admissions-bundle-in'
  ));
  const premiumOffer = props.offers.find((offer) => (
    offer.slug === 'premium' || offer.slug === 'premium-in'
  ));
  const trialOffer = props.offers.find((offer) => offer.slug === '1-week');

  return (
    <div style={{ overflow: 'hidden', position: 'relative' }}>
      {/* Absolutely positioned background elements */}
      <>
        <div
          className={marketingStyles.greenGradientBg}
          style={{ position: 'absolute', height: 900, left: 0, top: 0, width: '100%' }}
        />
        <div className={styles.greenEllipse} />
      </>

      <PageLayout
        childrenStyle={{ paddingTop: 30 }}
        hideSignUp={props.hideSignUp}
        isLoggedIn={props.isLoggedIn}
        navigationItems={props.navigationItems}
        navigationStyle={{ background: '#fff' }}
        notice={props.notice}
      >
        <HeroSection />

        <SocialProofSection />

        <OffersSection offers={props.offers} />

        <CompetitorComparisonSection
          admissionsBundleOfferFullPriceInCents={admissionsBundleOffer.fullPriceInCents}
          admissionsBundleOfferRegistrationUrl={admissionsBundleOffer.registrationUrl}
        />

        <TestimonialsSection />

        <ApplicationSupportSection />

        <FAQSection
          entries={[
            {
              answer: (
                <>
                  <p>Anyone can use Magoosh, but it's best for individuals who fall into one of the following categories:</p>
                  <ul>
                    <li><b>Self-studiers:</b> those who prefer to study on their own time and at their own pace.</li>
                    <li><b>Video-lovers:</b> those who learn better by watching video than through reading or live-learning. Every practice question has a video explanation.</li>
                    <li><b>Busy students and professionals:</b> those who do not have much time to study and want to sneak in a practice session here and there.</li>
                  </ul>
                </>
              ),
              question: 'Who should use Magoosh?'
            },
            {
              answer: (
                <>
                  If you’re unhappy for any reason, email us within 7 days of the <i>original</i> purchase date (regardless if you pay in installments), and we will give you a full refund. No strings attached. If you purchase Magoosh using a non-US credit card and request a refund, sometimes your credit card company will keep the foreign currency transaction fee.
                </>
              ),
              question: 'How does the refund policy work?'
            },
            {
              answer: "We compare your performance in the program with data from Magoosh students' actual scores and performance in our program and then provide you with a predicted range for the actual test.",
              question: 'How does your score predictor work?'
            },
            {
              answer: (
                <>
                  Check out this <a href="https://magoosh.zendesk.com/hc/en-us/articles/360048879351">help article with a list of accessibility features!</a>
                </>
              ),
              question: 'What accessibility features are there?'
            },
            {
              answer: (
                <>
                  You can do that! <a href="/register/verbal">Sign up for a Verbal+AWA plan here</a>, or <a href="/register/math">sign up for a Math plan here</a>. Both plans last for 6 months and come with a +2 score guarantee. Please note that these plans do not include practice tests or score predictors.
                </>
              ),
              question: 'Can I sign up for a Verbal+AWA or Math only plan?'
            },
            {
              answer: 'Visa, Mastercard, American Express, Discover, and PayPal. We process our transactions through Paypal, so your credit card info is safe.',
              question: 'What types of payment do you accept?'
            },
            {
              answer: (
                <>
                  <p>If you choose to pay in four monthly installments, your first payment is due at checkout, with the remaining installments charged monthly. You'll receive an email with your payment schedule after checkout. </p><br />
                  <p><strong>Please note:</strong> Installment plans are not subscriptions—payments continue even if you pause your plan, but your access will be extended by the paused duration. By choosing an installment plan, you are committing to paying the full purchase amount unless you request a refund within 7 days of your initial purchase. Pausing your plan does not extend the refund window.</p>
                </>
              ),
              question: 'How do monthly payments work?'
            },
            {
              answer: 'Yes we do! You can access all of the lessons anywhere with our apps, and you can use your mobile browser to get full access to the site.',
              question: 'Do you have iPad/iPhone/Android access?'
            },
            {
              answer: "We're happy to help you with questions you have about any of our material. Send us a message while you're studying, and we'll get back to you quickly. We do have limitations to how much we can help any individual student, but in most cases (for 99% of students), we can answer all of your questions!",
              question: "If I'm struggling with a question, can I get help?"
            },
            {
              answer: (
                <>
                  You definitely can! Email us at <a href="mailto:help@magoosh.com">help@magoosh.com</a> and we'll help set you up.
                </>
              ),
              question: 'Can I purchase multiple accounts for my students or non-profit?'
            },
            {
              answer: (
                <>
                  Yes! <a href="/shorter-gre" target="_blank">The GRE became shorter in 2023</a>. Our content is fully optimized for the shorter GRE. Our video lessons and practice tests have been updated to reflect what you'll see on test day.
                </>
              ),
              question: 'Can I use Magoosh to study for the shorter GRE?'
            },
            {
              answer: 'Yes! The premium plan includes all of our math, verbal, and AWA content.',
              question: 'Does the premium plan include everything from the math and verbal plans?'
            },
            {
              answer: 'Our AI grades your essay like a real GRE grader and then provides feedback to boost your score.  You also get expert-led lessons with strategies and tips for writing a high-scoring essay.',
              question: 'What about the writing section and essay grading?'
            },
            {
              answer: 'All prices are in USD (United States Dollars).',
              question: 'What currency are the prices listed in?'
            },
            {
              answer: "It's 100% online. No DVDs. No books. It's all online, so you can practice taking the test on a computer, just like you'll have to do on exam day for the real GRE. It works on both Macs and PCs: any device with an internet connection!",
              question: 'How does the course work?'
            },
            {
              answer: "With Magoosh Premium, you'll have access to full-length practice tests that accurately reflect what you'll see on test day, including an ungraded writing section. The tests are generated from your pool of unanswered questions so we recommend creating up to 6 practice tests, and saving the remaining for practice.",
              question: 'Do I get practice tests?'
            },
            {
              answer: "No! Many of our students are first-time GRE test-takers, so if you're ready to start studying now, you should go ahead and sign up! Our lessons start from the beginning and teach you everything you need to know about the GRE, so taking the test prior is not required.",
              question: 'Do I need to have taken the test before?'
            },
            {
              answer: 'We know that everyone has a different approach to learning, so we offer study schedules that are flexible enough to fit your own schedule, so you can spend more time learning what you need to.',
              question: 'What are your study schedules like?'
            },
            {
              answer: 'Extremely close. Our experienced instructors use metrics to ensure the difficulty and types of questions match those on the actual test. We rank the questions by difficulty to help you master the basics and progress towards your target score.',
              question: 'How close is the material to the actual GRE?'
            }
          ]}
        />

        <StartYourPrepSection
          admissionsBundleOfferRegistrationUrl={admissionsBundleOffer.registrationUrl}
          premiumOfferRegistrationUrl={premiumOffer.registrationUrl}
          trialOfferRegistrationUrl={trialOffer.registrationUrl}
        />
      </PageLayout>
    </div>
  );
};
